import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { trackLead } from '../utils/analytics';

const initialForm = {
  name: '',
  email: '',
};

export default function EmailCaptureForm({
  variant = 'home',
  source,
  className = '',
}) {
  const { language, t } = useLanguage();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const content = variant === 'guide'
    ? {
        kicker: t('emailCapture.guideKicker'),
        heading: t('emailCapture.guideHeading'),
        body: t('emailCapture.guideBody'),
        source: source || t('emailCapture.sourceGuide'),
        formName: 'email_capture_guide',
      }
    : {
        kicker: t('emailCapture.homeKicker'),
        heading: t('emailCapture.homeHeading'),
        body: t('emailCapture.homeBody'),
        source: source || t('emailCapture.sourceHome'),
        formName: 'email_capture_home',
      };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      const response = await fetch('https://sunshine-tours.onrender.com/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          source: content.source,
          language,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Could not save subscriber.');
      }

      setForm(initialForm);
      setStatus(data?.alreadySubscribed ? 'exists' : 'sent');
      trackLead({
        leadType: 'email_signup',
        formName: content.formName,
        source: content.source,
      });
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={`email-capture-section ${className}`.trim()}>
      <div className="email-capture-card">
        <div className="email-capture-copy">
          <span className="key-west-kicker">{content.kicker}</span>
          <h2>{content.heading}</h2>
          <p>{content.body}</p>
        </div>

        <form className="email-capture-form" onSubmit={handleSubmit}>
          <label className="email-capture-field">
            <span>{t('emailCapture.nameLabel')}</span>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder={t('emailCapture.namePlaceholder')}
              className="email-capture-input"
            />
          </label>

          <label className="email-capture-field">
            <span>{t('emailCapture.emailLabel')}</span>
            <input
              type="email"
              name="email"
              required
              value={form.email}
              onChange={handleChange}
              placeholder={t('emailCapture.emailPlaceholder')}
              className="email-capture-input"
            />
          </label>

          <button type="submit" className="book-btn email-capture-button" disabled={loading}>
            {loading ? t('emailCapture.sending') : t('emailCapture.submit')}
          </button>

          <p className="email-capture-note">{t('emailCapture.privacyNote')}</p>

          {status === 'sent' && (
            <p className="contact-status success" aria-live="polite">
              {t('emailCapture.success')}
            </p>
          )}

          {status === 'exists' && (
            <p className="contact-status success" aria-live="polite">
              {t('emailCapture.alreadySubscribed')}
            </p>
          )}

          {status === 'error' && (
            <p className="contact-status error" aria-live="polite">
              {t('emailCapture.error')}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
